import { z } from "zod";
import { ApiMoney } from "./tasks";

const MinorString = z.string().regex(/^\d+$/).max(12);

/** POST /v1/provider/tasks/:id/invoice — iş bitince (K-013, K-029) */
export const SubmitInvoiceBody = z.object({
  workedMinutes: z
    .number()
    .int()
    .positive()
    .max(24 * 60),
  expenses: z
    .array(
      z.object({
        description: z.string().min(1).max(300),
        amountMinor: MinorString,
        /** fiş fotoğrafı (Storage yolu) zorunlu */
        receiptPath: z.string().min(1).max(255),
      }),
    )
    .max(20)
    .default([]),
});
export type SubmitInvoiceBody = z.infer<typeof SubmitInvoiceBody>;

/** POST /v1/tasks/:id/invoice/confirm — müşteri onayı, isteğe bağlı bahşiş (K-028) */
export const ConfirmInvoiceBody = z.object({ tipMinor: MinorString.optional() });

/** POST /v1/tasks/:id/invoice/dispute — "tamamlanmadı" (K-013, K-047) */
export const DisputeInvoiceBody = z.object({
  reason: z.enum(["not_done", "poor_quality", "overcharged", "damage", "no_show", "other"]),
  description: z.string().max(2000).optional(),
});

export const InvoiceLineView = z.object({
  type: z.string(),
  description: z.string().nullable(),
  quantity: z.number(),
  amount: ApiMoney,
  tax: ApiMoney,
  receiptPath: z.string().nullable(),
});
export const InvoiceView = z.object({
  id: z.string().uuid(),
  status: z.string(),
  receiptNumber: z.string().nullable(),
  workedMinutes: z.number().nullable(),
  submittedAt: z.string().nullable(),
  autoConfirmAt: z.string().nullable(),
  subtotal: ApiMoney,
  customerFees: ApiMoney,
  /** kampanya indirimi (K-016), Poji ücretinden düşülmüş tutar */
  discount: ApiMoney,
  expenses: ApiMoney,
  tip: ApiMoney,
  tax: ApiMoney,
  total: ApiMoney,
  providerPayout: ApiMoney,
  lines: z.array(InvoiceLineView),
});
export type InvoiceView = z.infer<typeof InvoiceView>;

/** PUT /v1/provider/tasks/:id/receipt — hizmet verenin kendi fişi/faturası (K-114); dosya önce receipts/<iş>/… */
export const UploadProviderReceiptBody = z.object({
  path: z
    .string()
    .max(255)
    .regex(/^[0-9a-f-]{36}\/[A-Za-z0-9._-]{1,200}$/),
  number: z.string().trim().min(1).max(64).optional(),
});
export type UploadProviderReceiptBody = z.infer<typeof UploadProviderReceiptBody>;

const DocMoney = z.object({ amountMinor: z.string(), currency: z.string().length(3) });

/** GET /v1/tasks/:id/documents — işin belgeleri (iki taraf görür); dosyalar Storage'dan istemcide açılır */
export const TaskDocuments = z.object({
  /** Poji makbuzu (vergi faturası değil, K-042) */
  pojiReceipt: z
    .object({ number: z.string(), issuedAt: z.string().nullable(), total: DocMoney })
    .nullable(),
  /** hizmet verenin kendi fişi/faturası (receipts kovası) */
  providerReceipt: z
    .object({ path: z.string(), number: z.string().nullable(), uploadedAt: z.string() })
    .nullable(),
  /** masraf fişleri (K-029) */
  expenseReceipts: z.array(
    z.object({ description: z.string().nullable(), path: z.string(), amount: DocMoney }),
  ),
  /** iade/düzeltme belgeleri (K-089) */
  creditNotes: z.array(
    z.object({
      number: z.string(),
      reason: z.string(),
      amount: DocMoney,
      issuedAt: z.string(),
      pdfPath: z.string().nullable(),
    }),
  ),
});
export type TaskDocuments = z.infer<typeof TaskDocuments>;

/** GET /v1/me/receipts?year=&locale= — müşterinin tüm makbuzları ve yıllık toplam (K-164) */
export const MyReceiptsQuery = z.object({
  year: z.coerce.number().int().min(2020).max(2100).optional(),
  locale: z.string().min(2).max(10).optional(),
});
export interface MyReceipts {
  /** makbuzu olan yıllar (yeniden eskiye) */
  years: number[];
  year: number;
  items: {
    taskId: string;
    number: string;
    issuedAt: string;
    service: string | null;
    providerName: string;
    total: { amountMinor: string; currency: string };
    /** bu makbuz için kesilen iade/düzeltme toplamı */
    refunded: { amountMinor: string; currency: string } | null;
  }[];
  /** para birimi başına yıl toplamı (iadeler düşülmüş net) */
  totals: { currency: string; totalMinor: string; refundedMinor: string; netMinor: string }[];
}
